import React, { useState, useMemo, useEffect } from 'react';
import { useTimezone } from '@app/contexts/useTimezone';
import TabCard from '../TabCard';
import InfoButton from '../InfoButton';
import DeviationHeatmapView from './DeviationHeatmapView';
import BaselineComparisonView from './BaselineComparisonView';
import Tooltip from './Tooltip';
import { TooltipData, DeviationTooltipContent, BaselineTooltipContent } from './types';
import { useMarketAggregates } from '../../contexts/MarketAggregatesContext';
import { calculateDeviationData, calculateBaselineComparison } from './utils';

interface HeatmapCardProps {
  tabId?: string;
}

const HeatmapCard: React.FC<HeatmapCardProps> = ({ tabId }) => {
  const { state } = useMarketAggregates();
  const { timezone, formatInUserTimezone } = useTimezone();
  const [tooltip, setTooltip] = useState<TooltipData>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });

  // State for baseline selection and aggregation mode
  const [selectedBaseline, setSelectedBaseline] = useState<string>('');
  const [baselineAggregationMode, setBaselineAggregationMode] = useState<'hourly' | 'daily'>(
    'hourly',
  );
  const [deviationAggregationMode, setDeviationAggregationMode] = useState<'hourly' | 'daily'>(
    'daily',
  );
  const [alignmentMode, setAlignmentMode] = useState<'native' | 'common'>('native');

  // Persist/restore UI state per tab
  useEffect(() => {
    if (!tabId) return;
    try {
      const raw = localStorage.getItem(`ma-heatmap-ui-${tabId}`);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          selectedBaseline?: string;
          baselineAggregationMode?: 'hourly' | 'daily';
          deviationAggregationMode?: 'hourly' | 'daily';
          alignmentMode?: 'native' | 'common';
        };
        if (parsed.selectedBaseline) setSelectedBaseline(parsed.selectedBaseline);
        if (parsed.baselineAggregationMode)
          setBaselineAggregationMode(parsed.baselineAggregationMode);
        if (parsed.deviationAggregationMode)
          setDeviationAggregationMode(parsed.deviationAggregationMode);
        if (parsed.alignmentMode) setAlignmentMode(parsed.alignmentMode);
      }
    } catch {
      // Intentionally ignore localStorage/JSON errors
    }
  }, [tabId]);

  useEffect(() => {
    if (!tabId) return;
    try {
      localStorage.setItem(
        `ma-heatmap-ui-${tabId}`,
        JSON.stringify({
          selectedBaseline,
          baselineAggregationMode,
          deviationAggregationMode,
          alignmentMode,
        }),
      );
    } catch {
      // Intentionally ignore localStorage errors
    }
  }, [tabId, selectedBaseline, baselineAggregationMode, deviationAggregationMode, alignmentMode]);

  // Calculate deviation data from chart data and selected models
  const {
    deviationData,
    dates,
    timesteps: deviationTimesteps,
    dailyMeans,
    timestepMeans,
    statisticalContext: deviationStatisticalContext,
  } = useMemo(() => {
    // Don't calculate if data is still loading or empty
    if (state.chartData.loading || state.chartData.data.length === 0) {
      return {
        deviationData: [],
        dates: [],
        timesteps: [],
        dailyMeans: {},
        timestepMeans: {},
        statisticalContext: { std: 1, thresholds: [0.5, 1, 2] as [number, number, number] },
      };
    }
    return calculateDeviationData(state.chartData.data, state.availableModels);
  }, [state.chartData.data, state.chartData.loading, state.availableModels]);

  // Calculate baseline comparison data
  const baselineData = useMemo(() => {
    // Don't calculate if data is still loading or empty
    if (state.chartData.loading || state.chartData.data.length === 0) {
      return {
        comparisonData: [],
        timesteps: [],
        availableBaselines: [],
        statisticalContext: { std: 1, thresholds: [0.5, 1, 2] as [number, number, number] },
      };
    }
    const result = calculateBaselineComparison(
      state.chartData.data,
      state.availableModels,
      selectedBaseline,
      baselineAggregationMode,
    );
    // Auto-select first available baseline if none selected or if selected one is not available
    if (!selectedBaseline || !result.availableBaselines.includes(selectedBaseline)) {
      setSelectedBaseline(result.availableBaselines[0] || '');
    }
    return result;
  }, [
    state.chartData.data,
    state.chartData.loading,
    state.availableModels,
    selectedBaseline,
    baselineAggregationMode,
    timezone,
    formatInUserTimezone,
  ]);

  const {
    comparisonData,
    timesteps: baselineTimesteps,
    availableBaselines,
    statisticalContext: baselineStatisticalContext,
  } = baselineData;

  // Handler for deviation heatmap
  const handleDeviationCellHover = (
    event: React.MouseEvent,
    modelLabel: string,
    date: string,
    deviation: number,
    actualValue: number,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    // Format date/time based on aggregation mode with timezone awareness
    const formattedDate =
      deviationAggregationMode === 'daily'
        ? timezone === 'UTC'
          ? new Date(date).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
              timeZoneName: 'short',
            })
          : formatInUserTimezone
            ? formatInUserTimezone(new Date(date).toISOString(), 'EEE, MMM d zzz')
            : new Date(date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })
        : timezone === 'UTC'
          ? new Date(date).toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
              timeZoneName: 'short',
            })
          : formatInUserTimezone
            ? formatInUserTimezone(new Date(date).toISOString(), 'EEE, MMM d, HH:mm zzz')
            : new Date(date).toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });

    // Get the actual mean value for this date
    const realMeanValue = dailyMeans[date] || actualValue - deviation;

    const tooltipContent: DeviationTooltipContent = {
      date: formattedDate,
      modelLabel,
      deviation,
      actualValue,
      meanValue: realMeanValue,
    };

    setTooltip({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10, // Position above the cell
      content: tooltipContent,
    });
  };

  // Handler for baseline comparison heatmap
  const handleBaselineComparisonCellHover = (
    event: React.MouseEvent,
    modelLabel: string,
    timestep: string,
    difference: number,
    actualValue: number,
    baselineValue: number,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    // Format timestep with timezone awareness
    const formattedTimestep =
      timezone === 'UTC'
        ? new Date(timestep).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC',
            timeZoneName: 'short',
          })
        : formatInUserTimezone
          ? formatInUserTimezone(new Date(timestep).toISOString(), 'EEE, MMM d, HH:mm zzz')
          : new Date(timestep).toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });

    const tooltipContent: BaselineTooltipContent = {
      timestep: formattedTimestep,
      modelLabel,
      difference,
      actualValue,
      baselineValue,
      baselineLabel: selectedBaseline,
    };

    setTooltip({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10, // Position above the cell
      content: tooltipContent,
    });
  };

  const handleCellLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: null });
  };

  const tabs = [
    {
      id: 'deviation',
      label: 'Heatmap Deviations',
      content: (
        <div className="heatmap-content-container relative overflow-visible">
          <DeviationHeatmapView
            deviationData={deviationData}
            dates={dates}
            timesteps={deviationTimesteps}
            aggregationMode={deviationAggregationMode}
            alignmentMode={alignmentMode}
            onAggregationModeChange={setDeviationAggregationMode}
            onAlignmentModeChange={setAlignmentMode}
            dailyMeans={dailyMeans}
            timestepMeans={timestepMeans}
            onCellHover={handleDeviationCellHover}
            onCellLeave={handleCellLeave}
            statisticalContext={deviationStatisticalContext}
          />
        </div>
      ),
    },
    {
      id: 'disagreement',
      label: 'Heatmap Disagreements',
      content: (
        <div className="heatmap-content-container relative overflow-visible">
          <BaselineComparisonView
            comparisonData={comparisonData}
            timesteps={baselineTimesteps}
            baselineLabel={selectedBaseline}
            availableBaselines={availableBaselines}
            aggregationMode={baselineAggregationMode}
            alignmentMode={alignmentMode}
            chartData={state.chartData.data}
            onBaselineChange={setSelectedBaseline}
            onAggregationModeChange={setBaselineAggregationMode}
            onAlignmentModeChange={setAlignmentMode}
            onCellHover={handleBaselineComparisonCellHover}
            onCellLeave={handleCellLeave}
            statisticalContext={baselineStatisticalContext}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <TabCard
        title="Heatmap"
        tabs={tabs}
        defaultActiveTab="deviation"
        additionalHeaderContent={
          <div className="flex items-center gap-2">
            <InfoButton title="Heatmap card">
              <div>
                <div>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Views:</span>
                </div>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      Heatmap Deviations
                    </span>
                    : deviation from the mean of selected models.
                  </li>
                  <li>
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      Heatmap Disagreements
                    </span>
                    : compares to selected{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                      Baseline
                    </span>
                    .
                  </li>
                </ul>
                <div className="mt-1">
                  <span className="font-medium text-gray-800 dark:text-gray-200">Controls:</span>
                  <span className="ml-1">
                    Change{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                      Aggregation
                    </span>{' '}
                    and select a{' '}
                    <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                      Baseline
                    </span>{' '}
                    to update the view.
                  </span>
                </div>
                <div className="mt-1">
                  <span className="font-medium text-gray-800 dark:text-gray-200">Tip:</span> Hover
                  cells for detailed tooltips. "Selected Run" shows the model prediction for that
                  cell; in Deviations view "Mean" shows the mean value across the selected runs and
                  "Deviation" shows the deviation of the selected run from the mean; in
                  Disagreements view you'll see the Baseline value and the difference.
                </div>
              </div>
            </InfoButton>
          </div>
        }
      />
      <Tooltip tooltip={tooltip} />
    </>
  );
};

export default HeatmapCard;
